import { useState } from "react";
import { useParams } from "react-router-dom";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  PageContainer,
  PageHeader,
  PageMainCard,
  PageMainStack,
} from "@volca/ui/components/ui/page-components";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import { SettingsPageLoading } from "@/business-settings/common/settings-page-loading";
import {
  SETTINGS_PAGE_TITLES,
  SIDEBAR_MODES,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { WorkbookAccessLink } from "@/payroll/workbook-access/workbook-access-link";
import { useUserManagementAccess } from "@/user/management-access";
import { useGetAccountUsers } from "@/user/query";
import { WebAppUsers } from "./web-app-users";
import { DeleteUserConfirmationModal } from "./web-app-users/delete-user-confirmation-modal";
import { useUsersWorkbookAccess } from "./workbook-access/use-users-workbook-access";

const Users = () => {
  const [searchValue, setSearchValue] = useState("");
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { businessId } = useParams();
  const {
    canManageUsers,
    canManageWorkbookAccess,
    isLoading: accessLoading,
    isError,
    retry,
  } = useUserManagementAccess(businessId);
  const workbookAccess = useUsersWorkbookAccess(businessId);
  const { data: users = [] } = useGetAccountUsers(businessId);

  const handleNewUser = () => {
    updateSidebar({
      section: "users",
      mode: SIDEBAR_MODES.new,
      selectedItemId: undefined,
      isOpen: true,
    });
  };

  if (accessLoading) return <SettingsPageLoading title="Users" />;
  if (!canManageUsers && !canManageWorkbookAccess)
    return (
      <PageContainer>
        <PageHeader title="Users" />
        <Alert color="danger">
          {isError
            ? "Could not check user management access. Try again."
            : "Only managers can manage users. After workbook access is set up for Payroll, the manager must also have whole-workbook Edit access."}
        </Alert>
        {isError && (
          <Button variant="secondary" onClick={() => void retry()}>
            Try again
          </Button>
        )}
      </PageContainer>
    );

  return (
    <>
      <PageContainer variant="workspace">
        <PageHeader
          title={SETTINGS_PAGE_TITLES.users}
          actions={
            <>
              <SearchInput
                placeholder="Search Users"
                value={searchValue}
                onChange={setSearchValue}
              />
              {workbookAccess.settings && businessId && (
                <WorkbookAccessLink>Workbook access</WorkbookAccessLink>
              )}
              {canManageUsers && (
                <Button variant="secondary" onClick={handleNewUser}>
                  Add user
                </Button>
              )}
            </>
          }
        />
        {workbookAccess.query.isError && (
          <Alert color="danger">
            Could not load workbook access.{" "}
            <Button
              variant="link"
              onClick={() => void workbookAccess.query.refetch()}
            >
              Try again
            </Button>
          </Alert>
        )}
        <PageMainCard>
          <PageMainStack className="min-h-0 flex-1 py-4">
            <WebAppUsers
              workbookAccess={
                workbookAccess.settings
                  ? {
                      settings: workbookAccess.settings,
                    }
                  : undefined
              }
              searchValue={searchValue}
              onSearchChange={setSearchValue}
            />
          </PageMainStack>
        </PageMainCard>
      </PageContainer>
      <DeleteUserConfirmationModal users={users} />
    </>
  );
};

export default Users;
